import invoicesService from '@/services/invoices';
import customersService from '@/services/customers';
import salesFlatRatesService from '@/services/salesFlatRates';
import salesMultiCustomerService from '@/services/salesMultiCustomer';
import { normalizeCatalogOptions } from '@/utils/catalogOptions';
import { extractCollection, extractPayload } from '@/utils/dataNormalization';
import { INVOICES_TEXT } from '@/texts/catalog/invoices';

const staticFilterOptions = {
  hasBusiness: [
    {
      value: 'yes',
      label: String(INVOICES_TEXT?.toolbar?.selectorOptions?.yes || ''),
    },
    {
      value: 'no',
      label: String(INVOICES_TEXT?.toolbar?.selectorOptions?.no || ''),
    },
  ],
  hasReceivedProof: [
    {
      value: 'yes',
      label: String(INVOICES_TEXT?.toolbar?.selectorOptions?.yes || ''),
    },
    {
      value: 'no',
      label: String(INVOICES_TEXT?.toolbar?.selectorOptions?.no || ''),
    },
  ],
  invoiceType: [
    {
      value: 'nacional',
      label: String(INVOICES_TEXT?.toolbar?.selectorOptions?.invoiceTypes?.nacional || ''),
    },
    {
      value: 'exportacion',
      label: String(INVOICES_TEXT?.toolbar?.selectorOptions?.invoiceTypes?.exportacion || ''),
    },
    {
      value: 'egreso',
      label: String(INVOICES_TEXT?.toolbar?.selectorOptions?.invoiceTypes?.egreso || ''),
    },
  ],
};

export const createDefaultInvoicesFilterOptions = () => ({
  customer: [],
  hasBusiness: staticFilterOptions.hasBusiness,
  business: [],
  hasReceivedProof: staticFilterOptions.hasReceivedProof,
  invoiceType: staticFilterOptions.invoiceType,
});

const normalizeRow = (row) => ({
  ...row,
  customer_id: Number(row?.customer_id || 0),
  sale_id: Number(row?.sale_id || 0),
});

const normalizeBooleanSelectorValue = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (['yes', 'si', 'sí', 'true', '1'].includes(normalized)) {
    return 1;
  }

  if (['no', 'false', '0'].includes(normalized)) {
    return 0;
  }

  return null;
};

const firstSelected = (values) => {
  if (!Array.isArray(values) || values.length === 0) {
    return '';
  }

  return String(values[0] || '').trim();
};

export const buildInvoicesListParams = ({ toolbarSearch = '', toolbarFilters = {} } = {}) => {
  const normalizedSearch = String(toolbarSearch || '').trim();
  const filters = toolbarFilters && typeof toolbarFilters === 'object' ? toolbarFilters : {};

  const params = {
    search: normalizedSearch,
    date_start: String(filters.date_from || '').trim(),
    date_end: String(filters.date_to || '').trim(),
    customer_id: Array.isArray(filters.customer_ids) ? filters.customer_ids : [],
    sale_id: Array.isArray(filters.business_ids) ? filters.business_ids : [],
    invoice_type: firstSelected(filters.invoice_types),
  };

  const hasSale = normalizeBooleanSelectorValue(firstSelected(filters.has_business));
  if (hasSale !== null) {
    params.has_sale = hasSale;
  }

  const hasReceivedProof = normalizeBooleanSelectorValue(firstSelected(filters.has_received_proof));
  if (hasReceivedProof !== null) {
    params.has_received_proof = hasReceivedProof;
  }

  return params;
};

const extractSnapshotMetrics = (response) => {
  const payload = extractPayload(response);
  const metrics = payload && typeof payload === 'object'
    ? payload.metrics
    : null;

  return metrics && typeof metrics === 'object' ? metrics : {};
};

export const fetchInvoicesSnapshot = async (params = {}) => {
  const response = await invoicesService.index(params);
  const rows = extractCollection(response);

  return {
    rows: (Array.isArray(rows) ? rows : []).map((row) => normalizeRow(row)),
    metrics: extractSnapshotMetrics(response),
  };
};

export const fetchInvoicesRows = async (params = {}) => {
  const snapshot = await fetchInvoicesSnapshot(params);
  return Array.isArray(snapshot?.rows) ? snapshot.rows : [];
};

const normalizeCustomerIds = (customerIds = []) => {
  return [...new Set(
    (Array.isArray(customerIds) ? customerIds : [])
      .map((value) => Number(value || 0) || 0)
      .filter((value) => value > 0)
  )];
};

const mergeCatalogOptionsByValue = (...groups) => {
  const merged = [];
  const seen = new Set();

  groups.forEach((items) => {
    (Array.isArray(items) ? items : []).forEach((item) => {
      const value = String(item?.value || '').trim();
      if (value === '' || seen.has(value)) {
        return;
      }

      seen.add(value);
      merged.push(item);
    });
  });

  return merged;
};

export const fetchInvoiceBusinessFilterOptions = async ({ customerIds = [] } = {}) => {
  const normalizedCustomerIds = normalizeCustomerIds(customerIds);
  const params = {};

  if (normalizedCustomerIds.length === 1) {
    params.customer_id = normalizedCustomerIds[0];
  }

  if (normalizedCustomerIds.length > 1) {
    params.customer = normalizedCustomerIds;
  }

  try {
    const [flatRatesResponse, multiCustomersResponse] = await Promise.all([
      salesFlatRatesService.businesses(params, { skipLoading: true }),
      salesMultiCustomerService.index(params, { skipLoading: true }),
    ]);

    const flatRateOptions = normalizeCatalogOptions(extractCollection(flatRatesResponse), { useCodeInLabel: false });
    const multiCustomerOptions = normalizeCatalogOptions(extractCollection(multiCustomersResponse), { useCodeInLabel: false });

    return mergeCatalogOptionsByValue(flatRateOptions, multiCustomerOptions);
  } catch (error) {
    return [];
  }
};

export const fetchInvoicesFilterOptions = async () => {
  const defaults = createDefaultInvoicesFilterOptions();

  try {
    const [customersResponse, businessOptions] = await Promise.all([
      customersService.selectors?.() || Promise.resolve({ data: [] }),
      fetchInvoiceBusinessFilterOptions(),
    ]);

    const customerOptions = normalizeCatalogOptions(extractCollection(customersResponse), { useCodeInLabel: false });

    return {
      ...defaults,
      customer: customerOptions,
      business: businessOptions,
    };
  } catch (error) {
    return defaults;
  }
};
